import React, { useState, useEffect } from "react";
import NoDataFound from "../../NoDataFound";
import Checkbox from "../../form/Checkbox";
import { Tooltip } from "react-tooltip";
import Button from "../../form/Button";
import { useSettingsModal } from "../../../Context/SettingsContext";
import { PositionTier } from "../../../constant/Constant";
import DeletePopup from "../../DeletePopup";
import { postData } from "../../../services/api";
import toastr from "toastr";

const QuestionItems = ({
  data,
  updateHandler = () => {},
  type,
  setEditData = () => {},
  setEnabledPopup = () => {},
  loadConcernList = () => {},
  loadPredictorsList = () => {},
  onWeightChange = () => {}, // New prop for weight changes
  totalWeight = 0, // Receive total weight from parent
}) => {
  const { tierType } = useSettingsModal();
  const [enabledDeletePopup, setenabledDeletePopup] = useState(false);
  const [currentId, setCurrentId] = useState("");

  // Handle weight input change
  const handleWeightInputChange = (id, value) => {
    const numValue = parseFloat(value) || 0;
    
    // Validate range
    if (numValue < 0 || numValue > 100) {
      toastr.error("Weight must be between 0 and 100");
      return;
    }

    // Get current item's weight
    const currentItem = data.find(item => item._id === id);
    const currentWeight = currentItem?.weight || 0;
    
    // Calculate what the new total would be
    const newTotal = totalWeight - currentWeight + numValue;
    
    if (newTotal > 100) {
      toastr.error(`Total weight cannot exceed 100%. Current total: ${totalWeight}%. Adding ${numValue - currentWeight}% would exceed the limit.`);
      return;
    }

    // Update the weight through parent component
    onWeightChange(id, numValue);
  };

  // Handle checkbox change - reset weight when unchecking
  const handleCheckboxChange = (id, isChecked) => {
    if (!isChecked) {
      // Reset weight to 0 when unchecking
      onWeightChange(id, 0);
    }
    updateHandler(id, isChecked, type);
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-4 p-4 overflow-auto scroll-smooth scrollbar max-h-[calc(100vh-100px)]">
        {data.length > 0 ? (
          data.map((itm, inx) => (
            <div
              key={inx}
              className={`flex items-center justify-between gap-2 border rounded-md p-3 ${
                !itm?.disabled && itm?.isDefault
                  ? "bg-blue-50"
                  : "bg-transparent"
              } ${
                itm?.isCompanyGlobal ? "border-lime-500" : "border-dark-teal-200"
              }`}
            >
              <Checkbox
                checkboxLableClass={"text-slate-800"}
                checkboxInputClass={""}
                checkboxClass={"!rounded-full"}
                divClass={"!items-start"}
                checkboxName={itm?._id}
                isChecked={itm?.isDefault}
                checkboxLabel={itm?.name}
                checkboxValue={itm?._id}
                getCheckboxValue={(val, event) =>
                  handleCheckboxChange(val, event.target.checked)
                }
                isDisabled={itm?.disabled}
              />

              <div className="flex items-center justify-between gap-2">
                {type === "predictors" && (
                  <>
                    <div className="flex-shrink-0 p-1 ml-4 flex items-center gap-2">
                      <p className="text-[15px] font-semibold text-pink-600">
                        Weight(%)
                      </p>
                      <input
                        type="number"
                        className="w-20 p-1 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        value={itm?.weight || 0}
                        onChange={(e) =>
                          handleWeightInputChange(itm?._id, e.target.value)
                        }
                        disabled={!itm?.isDefault}
                        max={100}
                        min={0}
                        step={0.1}
                      />
                    </div>
                  </>
                )}

                <div
                  className="flex-shrink-0"
                  data-tooltip-id={"info-tooltip-" + itm?._id}
                  data-tooltip-place="bottom"
                >
                  {itm?.description && (
                    <>
                      <Tooltip
                        id={"info-tooltip-" + itm?._id}
                        className="!text-xs !bg-dark-teal-600 z-50 rounded py-3 px-6"
                        render={() => (
                          <div className="w-60 p-1 rounded-md">
                            <div className="text-[14px] font-normal">
                              <p className="font-normal text-white">
                                {itm?.description}
                              </p>
                            </div>
                          </div>
                        )}
                      />
                      <i
                        className="fa-light fa-circle-info"
                        style={{ color: "#14b8a6" }}
                      ></i>
                    </>
                  )}
                </div>
                {itm?.isCompanyGlobal && (
                  <>
                    <div
                      className="flex items-center"
                      data-tooltip-id={`editButton${itm?._id}`}
                      data-tooltip-place="bottom"
                    >
                      <Button
                        buttonClasses={
                          "!p-0 !flex !items-center !justify-center !text-[14px] !bg-transparent !text-dark-teal-600 !w-8 !h-8 !font-bold"
                        }
                        buttonType={"button"}
                        buttonIcon={"fa-regular fa-pencil"}
                        buttonIconPosition={"left"}
                        buttonHasLink={false}
                        functions={() => {
                          setEnabledPopup({ open: true, type: type });
                          setEditData(itm);
                        }}
                      />
                      <Tooltip
                        id={`editButton${itm?._id}`}
                        className="!text-xs !bg-teal-500 z-50 rounded py-3 px-6"
                        render={() => (
                          <div className="gap-x-1 gap-y-1">
                            <div className="text-xs leading-none flex items-center">
                              <div className="font-medium">{`Click To Edit ${
                                type === "predictors" ? "Predictor" : "Concern"
                              } (${
                                itm?.heading_name ? itm?.heading_name : itm?.name
                              })`}</div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                    <div
                      className="flex items-center"
                      data-tooltip-id={`deleteButton${itm?._id}`}
                      data-tooltip-place="bottom"
                    >
                      <Button
                        buttonClasses={
                          "!p-0 !flex !items-center !justify-center !text-[14px] !bg-transparent !text-rose-600 !w-8 !h-8 !font-bold"
                        }
                        buttonType={"button"}
                        buttonIcon={"fa-solid fa-trash"}
                        buttonIconPosition={"left"}
                        buttonHasLink={false}
                        functions={() => {
                          setenabledDeletePopup(true);
                          setCurrentId(itm?._id);
                        }}
                      />
                      <Tooltip
                        id={`deleteButton${itm?._id}`}
                        className="!text-xs !bg-teal-500 z-50 rounded py-3 px-6"
                        render={() => (
                          <div className="gap-x-1 gap-y-1">
                            <div className="text-xs leading-none flex items-center">
                              <div className="font-medium">{`Click To Delete ${
                                type === "predictors" ? "Predictor" : "Concern"
                              } (${
                                itm?.heading_name ? itm?.heading_name : itm?.name
                              })`}</div>
                            </div>
                          </div>
                        )}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          ))
        ) : (
          <NoDataFound label={"No Data Found"} />
        )}
      </div>
      {enabledDeletePopup && (
        <DeletePopup
          isOpen={enabledDeletePopup}
          setIsOpen={(val) => setenabledDeletePopup(val)}
          currentId={currentId}
          getActionValue={(obj) => {
            if (obj && obj?.button_type === "delete") {
              const loadDeleteRecord = async () => {
                try {
                  const statusData = await postData("heading/delete", {
                    id: obj?.row_id,
                  });
                  if (statusData.data) {
                    loadConcernList();
                    loadPredictorsList();
                  } else {
                    toastr.error(statusData.message);
                  }
                } catch (error) {
                  toastr.error(error.message);
                }
              };
              loadDeleteRecord();
            }
          }}
        />
      )}
    </>
  );
};

export default QuestionItems;